import { handleHttpError } from "@/utils/api-error-handling";
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../store/index";
import { useProgress, ProgressFinisher } from "@marcoschulte/vue3-progress";
import { App, nextTick } from "vue";
import { useAuthorisationStrategy } from "@/authorisation";
import { router } from "@/router";
import { ACCOUNT_TYPE } from "@/constants";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";
import {
  getApiClientId,
  getApiClientUrl,
  getBasePath,
} from "@/config/runtimeConfig";

type ApiErrorResponse = {
  message?: string;
};

axios.defaults.withCredentials = true;

export const $http = axios.create();

const { authoriseOnSignIn, clearAuthorisationCache } =
  useAuthorisationStrategy();

let refreshPromise: null | Promise<unknown> = null;
const clearPromise = () => (refreshPromise = null);

export type SignOutTarget = {
  route: { name: string; query?: Record<string, string> };
  fallback: string;
};

export const computeSignOutTarget = (
  baseUrl: string,
  currentPath: string,
): SignOutTarget => {
  const base = baseUrl || "/";
  const path = currentPath || "/";
  let redirectPath: string | undefined;

  if (path.startsWith(base)) {
    const relative = path.slice(base.length);
    redirectPath = relative
      ? relative.startsWith("/")
        ? relative
        : `/${relative}`
      : undefined;
  } else if (path && path !== "/") {
    redirectPath = path;
  }

  const route = {
    name: "auth-sign-in",
    ...(redirectPath && redirectPath !== "/"
      ? { query: { redirect: redirectPath } as Record<string, string> }
      : {}),
  };

  const fallbackBase = `${base}/auth/sign-in`;
  const fallback =
    redirectPath && redirectPath !== "/"
      ? `${fallbackBase}?redirect=${encodeURIComponent(redirectPath)}`
      : fallbackBase;

  return { route, fallback };
};

export const redirectToDefaultOrganization = async () => {
  const type = store.state.authentication.selectedAccount?.type_name;
  const orgUuid = store.state.authentication.selectedAccount?.uuid;

  let targetUrl;
  if (type === ACCOUNT_TYPE.SYSTEM) {
    targetUrl = buildOrgUrl("system", orgUuid);
  } else if (type === ACCOUNT_TYPE.MSP) {
    targetUrl = buildOrgUrl("msp", orgUuid);
  } else if (type === ACCOUNT_TYPE.CUSTOMER) {
    targetUrl = buildOrgUrl("customer", orgUuid);
  } else {
    targetUrl = "/auth/sign-in";
  }

  const baseUrl = getBasePath();
  const fullUrl = baseUrl === "/" ? targetUrl : `${baseUrl}${targetUrl}`;
  globalThis.location?.replace(fullUrl);
};

export const signOut = () => {
  clearAuthorisationCache();
  localStorage.clear();

  const { route, fallback } = computeSignOutTarget(
    getBasePath(),
    router.currentRoute.value.path || "/",
  );

  return router.replace(route).catch(() => {
    globalThis.location?.replace(fallback);
  });
};

export async function refreshToken() {
  try {
    const response = await axios.post(
      `${getApiClientUrl()}/v1/idp/oauth/token`,
      {
        grant_type: "refresh_token",
        client_id: getApiClientId(),
        refresh_token: store.getters["authentication/token"]?.refresh_token,
      },
    );

    store.dispatch("authentication/refreshToken", response.data);
    await authoriseOnSignIn();

    return response.data.access_token;
  } catch {
    signOut();
  }
}

export function updateHttpConfig(token: string) {
  $http.defaults.headers["Authorization"] = `Bearer ${token}`;
}

export default {
  install(app: App) {
    app.config.globalProperties.$http = $http;
    // No baseURL: every call fully-qualifies its path via getApiClientUrl()
    // (VITE_API_CLIENT). Leaving baseURL empty lets the same-origin "/api/v1/..."
    // paths resolve against the current origin (and absolute VITE_API_CLIENT values
    // override it as before). Previously this was set from VITE_API_URL, a stale
    // identity host that axios ignored while VITE_API_CLIENT was absolute.

    const progresses: ProgressFinisher[] = [];

    /**
     * Attaches JWT as Bearer token in Authorization header to all subsequent requests.
     * @param {JWT} token JWT received from server
     */

    app.config.globalProperties.$http.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const token = store.getters["authentication/token"]?.access_token;
        progresses.push(useProgress().start());

        if (token && config.headers) {
          config.headers["Authorization"] = `Bearer ${token}`;

          return config;
        }

        return config;
      },
      (err: unknown) => {
        progresses.pop()?.finish();
        handleHttpError(err);

        return Promise.reject(err);
      },
    );

    app.config.globalProperties.$http.interceptors.response.use(
      (response: AxiosResponse) => {
        progresses.pop()?.finish();

        return response;
      },
      async (error: AxiosError) => {
        const authUser = store.getters["authentication/authUser"];

        // Handle access denied errors - redirect to user's default organization instead of logout
        const errorMessage = (error.response?.data as ApiErrorResponse)
          ?.message;
        if (
          error.response?.status === 401 &&
          (errorMessage === "You are not authorized to access this resource." ||
            errorMessage?.includes("not authorized"))
        ) {
          // Check if user is authenticated and has a valid token
          const token = store.getters["authentication/token"]?.access_token;
          const selectedAccount =
            store.getters["authentication/selectedAccount"];

          if (token && selectedAccount?.uuid) {
            // User is authenticated but accessing wrong organization - redirect to their default org
            nextTick(() => {
              redirectToDefaultOrganization();
            });
            return Promise.reject(error);
          } else {
            // User is not authenticated - sign out
            signOut();
            return Promise.reject(error);
          }
        }

        if (authUser?.expired < new Date().getTime() / 1000) {
          const config = error.config;

          if (config && !config._retry) {
            config._retry = true;

            if (!refreshPromise) {
              refreshPromise = refreshToken().finally(clearPromise);
            }

            const token = await refreshPromise;
            config.headers["Authorization"] = `Bearer ${token}`;

            return $http(config);
          }
        }

        progresses.pop()?.finish();

        if (!error?.config?._hasCustomErrorHandling) {
          handleHttpError(error);
        }

        return Promise.reject(error);
      },
    );
  },
};
